import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from "@mui/styles";
import { styled } from '@mui/material/styles';
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, InputAdornment, IconButton, Step, Stepper, StepContent, StepLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Button, Tooltip, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ArrowRight, Eye, EyeOff, Nfc, X } from 'lucide-react';

import FlagstoneTheme from '../../../Config/flagstoneTheme';
import { dataService } from '../../../Services/data/data.service';

export default function ScanBadgeDialog({ dialogOpen, closeDialog, subtitle, handleBadgeNumber }) {

    const classes = useStyles();
    const { t } = useTranslation();

    // User data + errors
    const [badgeNumber, setBadgeNumber] = useState("");

    const inputRef = useRef(null);

    const processScannedBadge = (e) => {
        console.log("processScannedBadge");
        let scannedBadgeNumber = e;
        if (e.key === "Enter") {
            handleBadgeNumber(scannedBadgeNumber);
            closeDialog();
        }
    }

    return (

        <Dialog className={classes.dialog}
            open={dialogOpen}
            onClose={closeDialog}

            classes={{ paper: classes.dialogPaper }}

            aria-labelledby="draggable-dialog-title"
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
                    {t("Scan badge")}
                </DialogTitle>
                <button onClick={() => closeDialog()} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            <DialogContent className={classes.dialog__contentContainer}>
                <div className={classes.dialog__content}>
                    <p className={classes.dialog__subtitle}>{subtitle}</p>
                    <div className={classes.dialog__form}>
                        <div className={classes.form__badgeContainer}>
                            <button className={`${classes.form__noBadgeButton} ${classes.scanning}`}>
                                {t("Scan your badge now")}
                            </button>
                            <input type="text" tabIndex={"-1"} style={{ opacity: "0", height: "0px", margin: "0px", padding: "0px", border: "none", position: "absolute" }} autoFocus autoComplete='off' onKeyDown={(e) => processScannedBadge(e)} name="barcode" id="barcode" ref={inputRef} />
                        </div>
                    </div>
                </div>
            </DialogContent >
        </Dialog >
    );
}

const useStyles = makeStyles(() => ({

    dialog: {
        margin: "auto"
    },

    dialogPaper: {
        width: '400px !important',
        height: 'auto',
        borderRadius: '6px !important',
    },

    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',

        padding: '16px 16px 0px 16px !important',
        marginBottom: '8px !important',
    },

    dialog__title: {
        fontSize: '24px !important',
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'normal',

        padding: '0px !important',
    },

    dialog__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',

        padding: '3px !important',
        height: '25px !important',

        borderRadius: '3px',

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },

    },

    dialog__contentContainer: {
        padding: '0px 16px !important',
        marginBottom: "16px",

        // remove scrollbar
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
    },

    dialog__content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',

        margin: '0px !important',
    },

    dialog__subtitle: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        color: '#0C0E29',

        margin: '0px 0px 16px 0px',
    },

    dialog__form: {
        width: "100%",

        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },

    form__row: {
        width: "100%",
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },

    form__badgeContainer: {
        width: '100%',
        margin: "4px 0px",
    },

    form__noBadgeButton: {
        width: "100%",
        height: "54px",

        fontSize: '18px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        color: '#0C0E29',

        backgroundColor: '#F8F8F8',
        border: 'none',
        cursor: 'pointer',

        boxSizing: 'border-box',
        borderRadius: '6px',

        border: "1px solid #EDEDED",

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#4654FB',
            border: '1px solid #4654FB',
            color: '#F8F8F8',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },
    },

    scanning: {
        backgroundImage: 'linear-gradient(90deg, #F3FBF2 25%, #E2F8E0 50%, #F3FBF2 75%)',
        backgroundSize: '200% 100%',
        border: '1px solid #E2F8E0',
        animation: '$moveGradient 2s linear infinite',
        animationTimingFunction: 'ease-in-out',
        animationDelay: '0.5s',

        '&:hover': {
            backgroundImage: 'linear-gradient(90deg, #F3FBF2 25%, #E2F8E0 50%, #F3FBF2 75%)',
            backgroundSize: '200% 100%',
            border: '1px solid #E2F8E0',
            animation: '$moveGradient 2s linear infinite',
            animationTimingFunction: 'ease-in-out',
            animationDelay: '0.5s',
            color: FlagstoneTheme.palette.text.primary,
        },
    },
    '@keyframes moveGradient': {
        '0%': {
            backgroundPosition: '200% 0%',
        },
        '100%': {
            backgroundPosition: '-200% 0%',
        },
    },

    form__badgeButton: {
        width: "100%",
        height: "54px",

        fontSize: '18px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        color: FlagstoneTheme.palette.primary.main,

        backgroundColor: '#EDF2FF',
        cursor: 'pointer',

        boxSizing: 'border-box',
        borderRadius: '6px',

        border: "1px solid #C3D3FF",

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#4654FB',
            border: '1px solid #4654FB',
            color: '#F8F8F8',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },
    },

    form__editBadgeButton: {
        width: "100%",
        height: "54px",

        fontSize: '18px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        color: FlagstoneTheme.palette.text.primary,

        backgroundColor: '#F3FBF2',
        cursor: 'pointer',

        boxSizing: 'border-box',
        borderRadius: '6px',

        border: "1px solid #C6EFC3",

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#4654FB',
            border: '1px solid #4654FB',
            color: '#F8F8F8',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },
    },

    form__inputContainer: {
        width: '100%',

        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },

    form__switchContainer: {
        width: '100%',
        height: "40px",

        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },

    form__roles: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        flexWrap: 'wrap',
    },

    form__roleContainer: {
        width: "calc(50% - 4px)",
        display: 'flex',
        flexDirection: 'row',
        gap: '4px',
        alignItems: 'center',

        // label ellipsis
        '& label': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        }
    },

    form__passwordInputContainer: {
        position: 'relative',
    },

    form__inputFieldContainer: {
        position: 'relative',
    },

    form__passwordInput: {
        width: '100%',
        height: '40px',
        borderRadius: '3px',
        border: '1px solid #A9A9A9',

        padding: '0px 8px',
        paddingLeft: '8px',
        boxSizing: 'border-box',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif !important',
        fontWeight: 'regular',
        color: '#0C0E29',

        "&:focus": {
            outline: "none",
            border: "1px solid #4654FB",
        },
    },

    form__showPasswordButton: {
        height: "32px",
        width: "32px",
        borderRadius: "4px",

        backgroundColor: "transparent",

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: '4px',

        cursor: 'pointer',
        border: 'none',

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#EDEDED',
        },
    },

    form__switchLabel: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: FlagstoneTheme.palette.text.primary,

        gap: "8px !important",
        margin: "0px !important",
    },

    form__label: {
        fontSize: '12px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: FlagstoneTheme.palette.text.primary,
    },

    form__input: {
        width: '100%',
        height: '40px',
        borderRadius: '3px',
        border: '1px solid #A9A9A9',

        padding: '0px 8px',
        paddingLeft: '8px',
        boxSizing: 'border-box',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif !important',
        fontWeight: 'regular',
        color: '#0C0E29',

        "&:focus": {
            outline: "none",
            border: "1px solid #4654FB",
        },
    },

    form__inputEndAdornment: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: '8px',
    },

    dialog__inputContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },

    dialog__buttonContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between !important',
        width: "100% !important",
        boxSizing: 'border-box',

        padding: '16px !important',
        paddingTop: '0px !important',
        margin: '0px !important',
    },

    dialog__button__secondary: {
        color: FlagstoneTheme.palette.primary.main + " !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: "white !important",
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',

        '&:hover': {
            backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
            color: "white !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    buttons: {
        textAlign: "center"
    },
    stepButton: {
        zIndex: 1,
        color: "#fff",
        width: 50,
        height: 50,
        display: "flex",
        borderRadius: "50%",
        justifyContent: "center",
        alignItems: "center",
    },

    dialog__button: {
        color: "white !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',

        // align right
        marginLeft: 'auto !important',

        '&:hover': {
            backgroundColor: "white !important",
            color: FlagstoneTheme.palette.primary.main + " !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },
}));
